import { useState, useEffect, useCallback } from 'react';
import { Warehouse, Plus, Pencil, Trash2, X, Check, Package, ArrowRightLeft, ChevronRight, Search, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { cantidad } from '../utils/format';

// ── Helpers ───────────────────────────────────────────────────────────────
function fmtFecha(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function StockBadge({ stock, minimo, controla }) {
  if (!controla) return <span className="text-xs text-slate-400">Sin control</span>;
  if (stock <= 0) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Sin stock</span>;
  if (minimo > 0 && stock <= minimo) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Bajo mín.</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">OK</span>;
}

// ── Modal crear / editar depósito ──────────────────────────────────────────
function DepositoModal({ isOpen, onClose, deposito, onSave }) {
  const [nombre, setNombre] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setNombre(deposito?.nombre ?? '');
  }, [isOpen, deposito]);

  const handleSave = async () => {
    if (!nombre.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    await onSave(nombre.trim());
    setSaving(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={deposito ? 'Editar depósito' : 'Nuevo depósito'}
      size="sm"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : deposito ? 'Guardar cambios' : 'Crear depósito'}
          </button>
        </>
      }
    >
      <div>
        <label>Nombre *</label>
        <input
          className="input"
          placeholder="Ej: Depósito Central"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          autoFocus
        />
      </div>
    </Modal>
  );
}

// ── Card de depósito ───────────────────────────────────────────────────────
function DepositoCard({ dep, selected, onSelect, onEdit, onDelete, canEdit }) {
  return (
    <div
      onClick={() => onSelect(dep)}
      className={`card cursor-pointer transition-all duration-200 hover:shadow-md group
        ${selected ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-500/10' : 'hover:ring-1 hover:ring-slate-200 dark:hover:ring-slate-700'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors
          ${selected ? 'bg-primary-500' : 'bg-slate-100 dark:bg-slate-700 group-hover:bg-primary-50 dark:group-hover:bg-primary-500/10'}`}>
          <Warehouse size={18} className={selected ? 'text-white' : 'text-slate-400 dark:text-slate-500 group-hover:text-primary-500'} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-sm truncate ${selected ? 'text-primary-700 dark:text-primary-300' : 'text-slate-800 dark:text-slate-200'}`}>
            {dep.nombre}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {dep.total_movimientos === 0
              ? 'Sin movimientos'
              : `${dep.total_movimientos} movimiento${dep.total_movimientos !== 1 ? 's' : ''}`}
          </p>
        </div>

        {canEdit && (
          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onEdit(dep)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => onDelete(dep)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 text-center">
          <p className={`text-lg font-bold tabular-nums ${selected ? 'text-primary-600 dark:text-primary-400' : 'text-slate-700 dark:text-slate-300'}`}>
            {dep.productos_con_mov}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">Productos</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 text-center">
          <p className="text-[10px] text-slate-400">Último mov.</p>
          <p className="text-[11px] font-medium text-slate-600 dark:text-slate-400 mt-0.5">{fmtFecha(dep.ultimo_movimiento)}</p>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
        <span>Ver stock</span>
        <ChevronRight size={13} className={`transition-transform ${selected ? 'rotate-90' : ''}`} />
      </div>
    </div>
  );
}

// ── Panel de stock del depósito seleccionado ───────────────────────────────
function StockPanel({ deposito }) {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    if (!deposito) return;
    setLoading(true);
    setSearch('');
    api.get('/depositos/stock.php', { params: { deposito_id: deposito.id } })
      .then(r => setProductos(r.data.productos))
      .catch(() => toast.error('Error al cargar stock del depósito'))
      .finally(() => setLoading(false));
  }, [deposito]);

  if (!deposito) {
    return (
      <div className="card flex flex-col items-center justify-center py-20 text-slate-300 dark:text-slate-600">
        <Warehouse size={48} />
        <p className="text-sm mt-3 text-slate-400 dark:text-slate-500">Seleccioná un depósito para ver su stock</p>
      </div>
    );
  }

  const filtrados = search
    ? productos.filter(p =>
        p.nombre.toLowerCase().includes(search.toLowerCase()) ||
        (p.sku && p.sku.toLowerCase().includes(search.toLowerCase()))
      )
    : productos;

  const sinStock  = filtrados.filter(p => p.controla_stock && p.stock_deposito <= 0).length;
  const bajoMin   = filtrados.filter(p => p.controla_stock && p.stock_minimo > 0 && p.stock_deposito > 0 && p.stock_deposito <= p.stock_minimo).length;
  const ok        = filtrados.filter(p => p.controla_stock && p.stock_deposito > p.stock_minimo).length;

  return (
    <div className="space-y-4">
      {/* Header del panel */}
      <div className="card !p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-xl bg-primary-500 flex items-center justify-center">
            <Warehouse size={15} className="text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white">{deposito.nombre}</h2>
            <p className="text-[11px] text-slate-400">Stock por producto en este depósito</p>
          </div>
        </div>

        {/* Mini stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-red-50 dark:bg-red-500/10 rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-red-600 dark:text-red-400 tabular-nums">{sinStock}</p>
            <p className="text-[10px] text-red-400 mt-0.5">Sin stock</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-500/10 rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-amber-500 dark:text-amber-400 tabular-nums">{bajoMin}</p>
            <p className="text-[10px] text-amber-400 mt-0.5">Bajo mín.</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-500/10 rounded-xl p-2.5 text-center">
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{ok}</p>
            <p className="text-[10px] text-emerald-400 mt-0.5">Stock OK</p>
          </div>
        </div>

        {/* Buscador */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-8 py-2 text-sm"
            placeholder="Buscar producto o SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : filtrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-600">
            <Package size={40} />
            <p className="text-sm mt-3 text-slate-400 dark:text-slate-500">
              {search ? 'Sin resultados' : 'Sin productos con movimientos en este depósito'}
            </p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th className="text-right">Stock en depósito</th>
                    <th className="text-right">Mínimo</th>
                    <th>Estado</th>
                    <th className="text-right">Movimientos</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map(p => (
                    <tr key={p.id}>
                      <td>
                        <div>
                          <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">{p.nombre}</p>
                          {p.sku && <p className="text-[11px] text-slate-400 font-mono">{p.sku}</p>}
                        </div>
                      </td>
                      <td className="text-slate-500 dark:text-slate-400 text-xs">{p.categoria}</td>
                      <td className="text-right tabular-nums font-semibold text-slate-800 dark:text-slate-200">
                        {cantidad(p.stock_deposito, p.unidad)}
                      </td>
                      <td className="text-right tabular-nums text-slate-400 text-xs">
                        {p.controla_stock && p.stock_minimo > 0 ? cantidad(p.stock_minimo, p.unidad) : '—'}
                      </td>
                      <td>
                        <StockBadge stock={p.stock_deposito} minimo={p.stock_minimo} controla={p.controla_stock} />
                      </td>
                      <td className="text-right tabular-nums text-slate-400 text-xs">{p.movimientos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700">
              <p className="text-xs text-slate-400">{filtrados.length} producto/s</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Page principal ────────────────────────────────────────────────────────
export default function Depositos() {
  const { can } = useAuth();
  const canEdit = can('depositos.gestionar');

  const [depositos,      setDepositos]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [selected,       setSelected]       = useState(null);
  const [modalOpen,      setModalOpen]      = useState(false);
  const [editDeposito,   setEditDeposito]   = useState(null);
  const [confirmDeposito, setConfirmDeposito] = useState(null);
  const [deleting,       setDeleting]       = useState(false);

  const fetchDepositos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/depositos/index.php');
      setDepositos(res.data);
      setSelected(prev => prev ? (res.data.find(d => d.id === prev.id) ?? null) : null);
    } catch { toast.error('Error al cargar depósitos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDepositos(); }, [fetchDepositos]);

  const handleCreate = async (nombre) => {
    try {
      const res = await api.post('/depositos/index.php', { nombre });
      setDepositos(prev => [...prev, res.data].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      toast.success('Depósito creado');
      setModalOpen(false);
    } catch (err) { toast.error(err?.message || 'Error al crear'); }
  };

  const handleEdit = async (nombre) => {
    try {
      await api.put(`/depositos/index.php?id=${editDeposito.id}`, { nombre });
      setDepositos(prev => prev.map(d => d.id === editDeposito.id ? { ...d, nombre } : d).sort((a, b) => a.nombre.localeCompare(b.nombre)));
      if (selected?.id === editDeposito.id) setSelected(s => ({ ...s, nombre }));
      toast.success('Depósito actualizado');
      setEditDeposito(null);
    } catch (err) { toast.error(err?.message || 'Error al actualizar'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/depositos/index.php?id=${confirmDeposito.id}`);
      setDepositos(prev => prev.filter(d => d.id !== confirmDeposito.id));
      if (selected?.id === confirmDeposito.id) setSelected(null);
      toast.success('Depósito eliminado');
      setConfirmDeposito(null);
    } catch (err) { toast.error(err?.message || 'Error al eliminar'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Depósitos</h1>
        {canEdit && (
          <button className="btn-primary" onClick={() => setModalOpen(true)}>
            <Plus size={16} /> Nuevo depósito
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ── Lista de depósitos ── */}
        <div className="space-y-3">
          {loading ? (
            <div className="card flex justify-center py-12">
              <div className="w-7 h-7 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
            </div>
          ) : depositos.length === 0 ? (
            <div className="card flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-600">
              <Warehouse size={40} />
              <p className="text-sm mt-3 text-slate-400 dark:text-slate-500">Sin depósitos registrados</p>
              {canEdit && (
                <button className="btn-primary mt-4 text-sm" onClick={() => setModalOpen(true)}>
                  <Plus size={14} /> Crear primero
                </button>
              )}
            </div>
          ) : (
            depositos.map(dep => (
              <DepositoCard
                key={dep.id}
                dep={dep}
                selected={selected?.id === dep.id}
                onSelect={setSelected}
                onEdit={(d) => setEditDeposito(d)}
                onDelete={(d) => setConfirmDeposito(d)}
                canEdit={canEdit}
              />
            ))
          )}
        </div>

        {/* ── Panel de stock ── */}
        <div className="lg:col-span-2">
          <StockPanel deposito={selected} />
        </div>
      </div>

      {/* Modal nuevo */}
      <DepositoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        deposito={null}
        onSave={handleCreate}
      />

      {/* Modal editar */}
      <DepositoModal
        isOpen={!!editDeposito}
        onClose={() => setEditDeposito(null)}
        deposito={editDeposito}
        onSave={handleEdit}
      />

      {/* Confirm delete */}
      <ConfirmDialog
        isOpen={!!confirmDeposito}
        onClose={() => setConfirmDeposito(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Eliminar depósito"
        message={`¿Eliminar "${confirmDeposito?.nombre}"? Solo es posible si no tiene movimientos registrados.`}
      />
    </div>
  );
}
